import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { X, ZoomIn, ChevronLeft, ChevronRight, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SEOHead, BreadcrumbSchema } from '../components/SEO';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'all', label: 'All' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'rooms', label: 'Rooms' },
  { value: 'dining', label: 'Dining' },
  { value: 'amenities', label: 'Amenities' },
  { value: 'nature', label: 'Nature' },
  { value: 'events', label: 'Events' },
];

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/gallery`);
        setGalleryImages(response.data);
      } catch (error) {
        console.error('Failed to fetch gallery:', error);
        // Fallback to empty array if API fails
        setGalleryImages([]);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const filteredImages = activeCategory === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const newIndex = (lightboxIndex + 1) % filteredImages.length;
    setLightboxIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (lightboxIndex - 1 + filteredImages.length) % filteredImages.length;
    setLightboxIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  };

  // Breadcrumbs for SEO
  const breadcrumbs = [
    { name: 'Home', url: 'https://thehotelemerald.in/' },
    { name: 'Gallery', url: 'https://thehotelemerald.in/gallery' }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="gallery-page">
      {/* SEO Components */}
      <SEOHead 
        title="Photo Gallery | The Hotel Emerald - Luxury Hotel in Eastern Ghats"
        description="Explore The Hotel Emerald through our photo gallery. View our luxury rooms, dining spaces, amenities, and the stunning Eastern Ghats surroundings."
        keywords="hotel emerald photos, semiliguda hotel gallery, eastern ghats hotel images, koraput hotel pictures"
        url="https://thehotelemerald.in/gallery"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[40vh] min-h-[350px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/gallery/hotel-22.jpeg"
            alt="Hotel Emerald Interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-emerald-950/60" />
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="overline text-gold mb-3"
          >
            Visual Journey
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-serif text-4xl md:text-5xl lg:text-6xl"
          >
            Gallery
          </motion.h1>
        </div>
      </section>

      {/* Instagram CTA */}
      <section className="py-8 bg-gradient-to-r from-emerald-900 to-emerald-800">
        <div className="container-custom">
          <a 
            href="https://instagram.com/thehotelemerald"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-4 text-white hover:text-gold transition-colors"
            data-testid="instagram-link"
          >
            <Instagram className="w-6 h-6" />
            <span className="font-medium">Follow us @thehotelemerald for more moments</span>
          </a>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {/* Filter Tabs */}
          <div className="flex justify-center mb-12">
            <Tabs value={activeCategory} onValueChange={setActiveCategory}>
              <TabsList className="bg-muted flex-wrap h-auto gap-1">
                {categories.map((cat) => (
                  <TabsTrigger 
                    key={cat.value} 
                    value={cat.value}
                    data-testid={`gallery-filter-${cat.value}`}
                  >
                    {cat.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading gallery...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredImages.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No images found in this category.</p>
            </div>
          )}

          {/* Masonry Grid */}
          {!loading && filteredImages.length > 0 && (
            <motion.div 
              layout
              className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4"
            >
              <AnimatePresence>
                {filteredImages.map((image, index) => (
                  <motion.div
                    key={image.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                    className="group relative break-inside-avoid cursor-pointer overflow-hidden rounded-sm"
                    onClick={() => openLightbox(image, index)}
                    data-testid={`gallery-image-${image.id}`}
                  >
                    <img
                      src={image.src}
                      alt={image.alt || image.title}
                      className="w-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-emerald-950/0 group-hover:bg-emerald-950/50 transition-colors duration-300" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <ZoomIn className="w-10 h-10 text-white" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-emerald-950/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="text-white font-serif text-lg">{image.title}</p>
                      <p className="text-emerald-200 text-sm capitalize">{image.category}</p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-emerald-950/95 flex items-center justify-center"
            onClick={closeLightbox}
            data-testid="lightbox"
          >
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-white hover:bg-white/10"
              onClick={closeLightbox}
              data-testid="lightbox-close"
            >
              <X className="w-8 h-8" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 text-white hover:bg-white/10"
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="w-10 h-10" />
            </Button>

            <motion.div
              key={selectedImage.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="max-w-5xl max-h-[80vh] px-16"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.src}
                alt={selectedImage.alt || selectedImage.title}
                className="max-w-full max-h-[70vh] object-contain rounded-sm"
              />
              <div className="text-center mt-4">
                <p className="text-white font-serif text-xl">{selectedImage.title}</p>
                <p className="text-emerald-300 text-sm capitalize">{selectedImage.category}</p>
              </div>
            </motion.div>

            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 text-white hover:bg-white/10"
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              data-testid="lightbox-next"
            >
              <ChevronRight className="w-10 h-10" />
            </Button>

            {/* Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
              {lightboxIndex + 1} / {filteredImages.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
